import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { map, merge } from 'lodash';

import { devLog, devWarn } from '@/utils/logging';
import { WorkItemStatus } from '@/apps/dispatcher/types/enums';
import { Contract } from '@/types/enums';
import { IWorkItem } from '@/apps/dispatcher/types/IWorkItem';
import { StopInfo } from '@/apps/dispatcher/types/ISchedule';
import { BATCH_EDIT_MAX_ITEMS } from '@/utils/configs/dispatcherJournalConfig';
import {
  ActiveWorkItemFetch,
  ActiveWorkItemMutation,
  isCompleteWorkItem,
  mergeWorkItemSignal,
  reconcileCompleteWorkItem,
  touchActiveFetches,
  workItemFromCreateSignal,
  WorkItemListScope,
} from '@/apps/dispatcher/redux/workItemReconciliation';
import {
  beginWorkItemMutationReducer,
  beginWorkItemsFetchReducer,
  completeWorkItemMutationReducer,
  completeWorkItemsFetchReducer,
  failWorkItemMutationReducer,
  failWorkItemsFetchReducer,
  reconcileCreatedWorkItemReducer,
  reconcileWorkItemReducer,
  resetWorkItemReconciliationReducer,
} from '@/apps/dispatcher/redux/workItemReconciliationReducers';

/**
 * Where one status' list fetch has got to. `undefined` (no entry) means no
 * fetch has been dispatched for that status yet.
 */
export type WorkItemFetchState = 'loading' | 'loaded' | 'failed';

export interface WorkItemState {
  workItems: IWorkItem[];
  activeWorkItemId: number | string | null | undefined;
  activeWorkItem: IWorkItem | null;
  batchWorkItemIds: IWorkItem['id'][];
  isSelectAllChecked: boolean;
  wizardOpen: boolean;
  loading: boolean;
  stopsLoading: boolean;
  stopsErrors: { id: number | string; error: string }[];
  isFetching: boolean;
  fetchInitialized: boolean;
  // Per-status, because `loading` and `error` cannot answer for a single list:
  // all four statuses are fetched in parallel at startup and each writes those
  // same two fields, so the first response to land clears them for the lists
  // still waiting.
  statusFetchStates: Partial<Record<WorkItemStatus, WorkItemFetchState>>;
  isSignalRConnected: boolean;
  workItemUpdating: IWorkItem['id'] | null;
  error: string | null;
  activeFetches: Partial<Record<WorkItemStatus, ActiveWorkItemFetch>>;
  loadedScopes: Partial<Record<WorkItemStatus, WorkItemListScope>>;
  activeMutations: Record<string, ActiveWorkItemMutation>;
}

const initialState: WorkItemState = {
  workItems: [],
  workItemUpdating: null,
  activeWorkItemId: null,
  activeWorkItem: null,
  batchWorkItemIds: [],
  isSelectAllChecked: false,
  wizardOpen: false,
  loading: false,
  stopsLoading: false,
  stopsErrors: [],
  isFetching: false,
  fetchInitialized: false,
  statusFetchStates: {},
  isSignalRConnected: false,
  error: null,
  activeFetches: {},
  loadedScopes: {},
  activeMutations: {},
};

const workItemsStateSlice = createSlice({
  name: 'workItems',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setStopsLoading(state, action: PayloadAction<boolean>) {
      state.stopsLoading = action.payload;
    },
    setFetching(state, action: PayloadAction<boolean>) {
      state.isFetching = action.payload;
    },
    setFetchInitialized(state, action: PayloadAction<boolean>) {
      state.fetchInitialized = action.payload;
    },
    setStatusFetchState(
      state,
      action: PayloadAction<{
        status: WorkItemStatus;
        fetchState: WorkItemFetchState;
      }>
    ) {
      state.statusFetchStates[action.payload.status] =
        action.payload.fetchState;
    },
    setSignalRConnected(state, action: PayloadAction<boolean>) {
      state.isSignalRConnected = action.payload;
    },
    setWorkItemUpdating(state, action: PayloadAction<IWorkItem['id'] | null>) {
      state.workItemUpdating = action.payload;
    },
    setWorkItems(state, action: PayloadAction<IWorkItem[]>) {
      const existingWorkItemIds = new Map(
        state.workItems.map((workItem) => [workItem.id, true])
      );

      const uniqueNewWorkItems = action.payload.filter(
        (workItem) => !existingWorkItemIds.has(workItem.id)
      );

      state.workItems = [...state.workItems, ...uniqueNewWorkItems];
      state.loading = false;
      state.workItemUpdating = null;
      state.error = null;
    },
    setWorkItem(state, action: PayloadAction<IWorkItem>) {
      state.workItems = map(state.workItems, (workItem) =>
        workItem.id === action.payload.id ? action.payload : workItem
      );
    },
    beginWorkItemsFetch: beginWorkItemsFetchReducer,
    completeWorkItemsFetch: completeWorkItemsFetchReducer,
    failWorkItemsFetch: failWorkItemsFetchReducer,
    beginWorkItemMutation: beginWorkItemMutationReducer,
    completeWorkItemMutation: completeWorkItemMutationReducer,
    failWorkItemMutation: failWorkItemMutationReducer,
    reconcileWorkItem: reconcileWorkItemReducer,
    reconcileCreatedWorkItem: reconcileCreatedWorkItemReducer,
    resetWorkItemReconciliation: resetWorkItemReconciliationReducer,
    /**
     * Add a full work item the slice doesn't hold yet, leaving an existing
     * one untouched. Used when a mutation arrives for an item outside the
     * live queue's window (e.g. a past item resolved via the queued-history
     * search): the row is materialized from the search results so the
     * following merge reducer has something to apply the changes to, and the
     * Done/Archive pages pick it up without refetching. No `isNew` flag - the
     * item isn't new, it just became relevant to another page's filters.
     */
    insertWorkItemIfAbsent(state, action: PayloadAction<IWorkItem>) {
      const exists = state.workItems.some(
        (workItem: IWorkItem) => workItem.id === action.payload.id
      );
      if (!exists) {
        state.workItems = [action.payload, ...state.workItems];
      }
    },
    setWorkItemsByStatus(
      state,
      action: PayloadAction<{ status: WorkItemStatus; workItems: IWorkItem[] }>
    ) {
      const { status, workItems } = action.payload;

      // Replace all existing work items with the specified status
      state.workItems = state.workItems.filter(
        (workItem) => workItem.status !== status
      );

      state.workItems = [...state.workItems, ...workItems];
      state.loading = false;
      state.workItemUpdating = null;
      state.error = null;
    },
    signalWorkItemRTPCreate(state, action: PayloadAction<IWorkItem>) {
      const newWorkItem = action.payload;

      if (!isCompleteWorkItem(newWorkItem)) {
        devWarn('Ignored incomplete RTP work item create:', newWorkItem.id);
        return;
      }

      touchActiveFetches(state, newWorkItem.id);

      const existingItemIndex = state.workItems.findIndex(
        (item) =>
          item.id !== undefined && String(item.id) === String(newWorkItem.id)
      );

      if (existingItemIndex === -1) {
        const updatedExistingItems = state.workItems.map((item) => ({
          ...item,
          isNew: false,
        }));

        state.workItems = [
          {
            isNew: true,
            ...newWorkItem,
          },
          ...updatedExistingItems,
        ];
      }
    },
    signalWorkItemAPICreate(
      state,
      action: PayloadAction<{
        id: number | string;
        status: WorkItemStatus;
        contract: Contract;
        changes: Record<string, any>;
        timestamp: string;
      }>
    ) {
      const { id, changes } = action.payload;
      const completeWorkItem = workItemFromCreateSignal(action.payload);
      if (isCompleteWorkItem(completeWorkItem)) {
        touchActiveFetches(state, completeWorkItem.id);
        const existingItemIndex = state.workItems.findIndex(
          (item) =>
            item.id !== undefined &&
            String(item.id) === String(completeWorkItem.id)
        );
        if (existingItemIndex === -1) {
          state.workItems = [
            { ...completeWorkItem, isNew: true },
            ...state.workItems.map((item) => ({ ...item, isNew: false })),
          ];
        } else {
          reconcileCompleteWorkItem(state, completeWorkItem);
        }
        return;
      }

      mergeWorkItemSignal(state, id, changes, 'API');
    },
    signalWorkItemUpdate(
      state,
      action: PayloadAction<{
        id: number | string;
        changes: Partial<IWorkItem>;
      }>
    ) {
      const { id, changes } = action.payload;
      mergeWorkItemSignal(state, id, changes, 'API');
    },
    signalWorkItemRTPUpdate(
      state,
      action: PayloadAction<{
        id: number | string;
        changes: Partial<IWorkItem>;
      }>
    ) {
      const { id, changes } = action.payload;
      mergeWorkItemSignal(state, id, changes, 'RTP');
    },
    clearAllIsNew(state) {
      const updatedExistingItems = state.workItems.map((item) => ({
        ...item,
        isNew: false,
      }));

      state.workItems = updatedExistingItems;
    },
    clearIsNew(state, action: PayloadAction<number | string>) {
      const workItemId = action.payload;
      const index = state.workItems.findIndex((item) => item.id === workItemId);
      if (
        index !== -1 &&
        state.workItems[index] &&
        state.workItems[index].isNew
      ) {
        state.workItems[index].isNew = false;
      }
    },
    setActiveWorkItemId(
      state,
      action: PayloadAction<number | string | null | undefined>
    ) {
      state.activeWorkItemId = action.payload;
    },
    setActiveWorkItem(state, action: PayloadAction<IWorkItem | null>) {
      state.activeWorkItem = action.payload;
      if (action.payload?.id) {
        devLog(
          'Rendering WorkItemWizard for:',
          action.payload.id,
          action.payload
        );
      }
    },
    setSelectAllChecked(state, action: PayloadAction<boolean>) {
      state.isSelectAllChecked = action.payload;
    },
    setBatchWorkItemIds(state, action: PayloadAction<IWorkItem['id'][]>) {
      state.batchWorkItemIds = action.payload;
    },
    addToActiveBatch(state, action: PayloadAction<IWorkItem['id']>) {
      const exists = state.batchWorkItemIds.includes(action.payload);
      if (!exists && state.batchWorkItemIds.length < BATCH_EDIT_MAX_ITEMS) {
        state.batchWorkItemIds.push(action.payload);
      }
    },
    removeFromActiveBatch(state, action: PayloadAction<number | string>) {
      const workItemId = action.payload;
      state.batchWorkItemIds = state.batchWorkItemIds.filter(
        (id) => id !== workItemId
      );
    },
    removeItemsFromBatch(state, action: PayloadAction<(number | string)[]>) {
      state.batchWorkItemIds = state.batchWorkItemIds.filter(
        (id): id is number | string =>
          id !== undefined && !action.payload.includes(id)
      );
    },
    toggleActiveBatchItem(state, action: PayloadAction<IWorkItem['id']>) {
      const workItemId = action.payload;
      const existingIndex = state.batchWorkItemIds.findIndex(
        (id) => id === workItemId
      );

      if (existingIndex >= 0) {
        state.batchWorkItemIds.splice(existingIndex, 1);
      } else if (state.batchWorkItemIds.length < BATCH_EDIT_MAX_ITEMS) {
        state.batchWorkItemIds.push(workItemId);
      }
    },
    clearActiveBatch(state) {
      state.isSelectAllChecked = false;
      state.batchWorkItemIds = [];
    },
    updateWorkItemStops(
      state,
      action: PayloadAction<{
        id: number | string | undefined;
        stopOptions: StopInfo[];
      }>
    ) {
      const index = state.workItems.findIndex(
        (item) => item.id === action.payload.id
      );
      if (index !== -1 && state.workItems[index]) {
        state.workItems[index].stopOptions = action.payload.stopOptions;
      }
    },
    addWorkItemStopsError(
      state,
      action: PayloadAction<{ id: number | string; error: string }>
    ) {
      const { id, error } = action.payload;
      const existingIndex = state.stopsErrors.findIndex(
        (item) => item.id === id
      );

      if (existingIndex >= 0) {
        state.stopsErrors[existingIndex] = { id, error };
      } else {
        state.stopsErrors.push({ id, error });
      }
    },
    removeWorkItemStopsError(state, action: PayloadAction<number | string>) {
      state.stopsErrors = state.stopsErrors.filter(
        (item) => item.id !== action.payload
      );
    },
    updateActiveWorkItem(state, action: PayloadAction<Partial<IWorkItem>>) {
      if (state.activeWorkItem) {
        state.activeWorkItem = merge(
          state.activeWorkItem,
          action.payload
        ) as IWorkItem;
      }
    },
    setWizardOpen(state, action: PayloadAction<boolean>) {
      state.wizardOpen = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.loading = false;
      state.workItemUpdating = null;
    },
  },
});

export const {
  setLoading,
  setStopsLoading,
  setFetching,
  setFetchInitialized,
  setStatusFetchState,
  setSignalRConnected,
  setWorkItemUpdating,
  setWorkItems,
  setWorkItem,
  beginWorkItemsFetch,
  completeWorkItemsFetch,
  failWorkItemsFetch,
  beginWorkItemMutation,
  completeWorkItemMutation,
  failWorkItemMutation,
  reconcileWorkItem,
  reconcileCreatedWorkItem,
  resetWorkItemReconciliation,
  insertWorkItemIfAbsent,
  setActiveWorkItemId,
  setActiveWorkItem,
  setBatchWorkItemIds,
  addToActiveBatch,
  removeFromActiveBatch,
  removeItemsFromBatch,
  toggleActiveBatchItem,
  clearActiveBatch,
  setWizardOpen,
  setError,
  signalWorkItemRTPCreate,
  signalWorkItemAPICreate,
  signalWorkItemUpdate,
  signalWorkItemRTPUpdate,
  clearAllIsNew,
  clearIsNew,
  updateActiveWorkItem,
  updateWorkItemStops,
  addWorkItemStopsError,
  removeWorkItemStopsError,
  setWorkItemsByStatus,
  setSelectAllChecked,
} = workItemsStateSlice.actions;

export default workItemsStateSlice.reducer;
